import {
  AfterViewInit,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { LayoutUsage } from '@barthauer/abc/arena';
import { AbcCore } from '@barthauer/abc/core';
import { ProfileInfo } from '@barthauer/abc/core/authentication';
import { PersistenceService, PersistObjectsPackage } from '@barthauer/abc/core/data';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { DataEntity, MapService } from '@barthauer/abc/graphics/adapters/open-layers';
import { ToastType } from '@barthauer/abc/ui-components';
import { BasysArenaComponent } from '@barthauer/basys/arena';
import { AbcArenaService, Status } from 'libs/abc/arena/src/lib/abc-arena.service';
import { ArenaService } from 'libs/abc/arena/src/lib/core/arena.service';
import { forkJoin, Observable, of, Subject, Subscription } from 'rxjs';
import { concatMap, map, tap } from 'rxjs/operators';
import { BasysMapFrameService } from '../../basys-map-frame.service';
import { GeometryHelper } from '../../shared/helpers';
declare let ol: any;

@Component({
    selector: 'app-add-object',
    templateUrl: './add-object.component.html',
    styleUrls: ['./add-object.component.scss'],
    standalone: false
})
export class AddObjectComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private arenaLoaded = new Subject<boolean>();
  public arenaLoaded$ = this.arenaLoaded.asObservable();
  public noLayoutPath: boolean;
  public res = ResourceSet.Empty;
  basysArena: BasysArenaComponent;
  objEntity: DataEntity;
  geomIdentifier: string;
  displayProgressbar = false;
  @Output() closeDialog = new EventEmitter();
  @ViewChild('dynamic', { static: true, read: ViewContainerRef })
  viewContainerRef: ViewContainerRef;

  private subscriptions: Subscription[] = [];

  constructor(
    private frame: BasysMapFrameService,
    private resourceService: ResourceManagerService,
    private abcCore: AbcCore,
    private arenaService: ArenaService,
    private mapService: MapService,
    private persistenceService: PersistenceService,
    private abcArenaService: AbcArenaService
  ) {
    this.noLayoutPath = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
    this.persistenceService.prepareModelBeforePersist = null;
    for (const s of this.subscriptions) {
      s.unsubscribe();
    }
  }

  ngAfterViewInit(): void {
    this.initArena();
  }

  async ngOnInit() {
    this.res = await this.resourceService.getResourceSet('basys-map-frame');
  }

  initArena() {
    this.basysArena = <BasysArenaComponent>(
      this.frame.loadComponent(BasysArenaComponent, this.viewContainerRef).instance
    );
    const objectType = this.objEntity.objectType.toString();
    this.basysArena.arenaName = ProfileInfo.defaultArenaName;
    this.basysArena.renderHigna = false;
    this.basysArena.renderHigon = false;
    this.basysArena.renderNavigation = false;
    this.basysArena.renderHeader = false;
    this.basysArena.renderDrawer = false;
    this.basysArena.isStandaloneDataForm = true;
    this.basysArena.crudPermissions = {
      DenyDelete: true,
      DenyInsert: true,
      DenyUpdate: false,
      IsReadOnly: false,
    };
    this.basysArena.layoutUsage = LayoutUsage.New;
    this.basysArena.defaultObjectType = objectType;
    this.basysArena.disableNotification = false;

    this.persistenceService.prepareModelBeforePersist = (model) => {
      const objects = model?.persistObjectsModel?.Objects;
      const entity = this.objEntity;
      if (objects && entity) {
        return of(model).pipe(
          concatMap((model_) => {
            const observables: Observable<PersistObjectsPackage>[] = [];
            for (let arr of objects) {
              for (let o of arr) {
                observables.push(
                  this.abcCore.objectSpace.getObjectType(o.ObjectKey?.ObjectType).pipe(
                    tap((ot) => {
                      if (ot?.isMatch(entity?.objectType)) {
                        o.TargetColumns.push({
                          Name: 'Geometry__Json',
                          Value: this.getFeatureGeometryAsJson(),
                        });
                      }
                    }),
                    map((_) => model_)
                  )
                );
              }
            }
            return forkJoin(observables);
          }),
          map((arr) => (arr?.length ? arr[0] : model))
        );
      }
      return of(model);
    };
    this.subscriptions.push(
      this.persistenceService.afterPersistPendingChanges$.subscribe(
        (result) => {
          if (Array.isArray(result)) {
            const dataEntities = result.map((_) => new DataEntity(_.objectType, _.id));
            this.appendObjectAnnotations(dataEntities);
          }
        },
        (err) => {
          this.abcCore.error.publish(err);
        }
      )
    );
    this.subscriptions.push(
      this.basysArena.arenaStatus().subscribe(
        (arena) => {
          if (arena.Status === Status.Success) {
            this.noLayoutPath = false;
            this.frame.lib.arenaLayoutCaption.next(arena.LayoutCaption);
          } else if (arena.Status === Status.Error) {
            this.noLayoutPath = true;
            this.frame.lib.arenaLayoutCaption.next('');
          }
        },
        (error) => {
          this.abcCore.error.publish(error);
        }
      )
    );

    this.basysArena.openLayoutByObjectType(this.basysArena.arenaName, objectType, null);
  }

  appendObjectAnnotations(entities: DataEntity[]) {
    this.displayProgressbar = true;
    this.mapService.prepareObjectGraphics(entities).subscribe(
      (res) => {
        this.mapService.refreshMapLayer(entities);
      },
      (err) => this.toastErrorMessage(err),
      () => {
        this.displayProgressbar = false;
        this.close();
      }
    );
  }

  toastErrorMessage(err: any): void {
    this.arenaService.notificationSubject.next({
      type: ToastType.Error,
      message: err.message,
    });
  }

  private getFeatureGeometryAsJson(): any {
    let feature = this.objEntity?.feature;
    let identifier = this.geomIdentifier;
    let ret = {};
    if (feature) {
      const geom = this.mapService.getCurrentView().transformGeometryToDataProjection(feature.getGeometry());
      if (geom) {
        const z = this.mapService.getZValue(feature.get('z_coord'));
        if (geom instanceof ol.geom.Point) ret = GeometryHelper.formPointGeometries(geom, identifier, z);
        else if (geom instanceof ol.geom.LineString) ret = GeometryHelper.formLineGeometries(geom, identifier);
        else if (geom instanceof ol.geom.Polygon) ret = GeometryHelper.formPolygonGeometries(geom, identifier);
      }
    }
    return ret;
  }

  init(objectInfo: DataEntity, geomIdentifier: string) {
    this.objEntity = objectInfo;
    this.geomIdentifier = geomIdentifier;
  }

  close() {
    this.closeDialog.emit();
  }
}
